/**
 * Enchantment registry — vanilla Java 1.21+/26.x data-pack values: max level,
 * weight, min/max cost (base + per level above first), which items it goes on
 * (primary = enchanting table, supported = anvil/books) and exclusive sets.
 * Ids are persisted in saves: APPEND ONLY.
 *
 * Pure data; DOM-free.
 */

import { I, itemNameOf } from '../itemIds';

export const E = {
  PROTECTION: 0, FIRE_PROTECTION: 1, FEATHER_FALLING: 2, BLAST_PROTECTION: 3, PROJECTILE_PROTECTION: 4,
  RESPIRATION: 5, AQUA_AFFINITY: 6, THORNS: 7, DEPTH_STRIDER: 8,
  SHARPNESS: 9, SMITE: 10, BANE_OF_ARTHROPODS: 11, KNOCKBACK: 12, FIRE_ASPECT: 13, LOOTING: 14, SWEEPING_EDGE: 15,
  EFFICIENCY: 16, SILK_TOUCH: 17, UNBREAKING: 18, FORTUNE: 19,
  POWER: 20, PUNCH: 21, FLAME: 22, INFINITY: 23, MENDING: 24,
  DENSITY: 25, BREACH: 26, WIND_BURST: 27,
  QUICK_CHARGE: 28, MULTISHOT: 29, PIERCING: 30,
  LOYALTY: 31, IMPALING: 32, RIPTIDE: 33, CHANNELING: 34,
  BINDING_CURSE: 35, VANISHING_CURSE: 36,
  LURE: 37, LUCK_OF_THE_SEA: 38,
  LUNGE: 39, // spear only (game/spear.ts)
} as const;
export type EnchId = typeof E[keyof typeof E];

/** Item categories (vanilla `#enchantable/*` tags). */
export type Cat =
  | 'armor' | 'head' | 'chest' | 'legs' | 'feet' | 'sword' | 'sharp' | 'mining' | 'mining_loot'
  | 'durability' | 'bow' | 'mace' | 'fire_aspect' | 'trident' | 'crossbow' | 'spear' | 'fishing_rod';

export interface EnchDef {
  id: number;
  key: string;
  name: string;
  max: number;
  weight: number;
  minBase: number; minPer: number;
  maxBase: number; maxPer: number;
  /** Items the enchanting table may put it on. */
  primary: Cat;
  /** Items an anvil/book may put it on (defaults to primary). */
  supported?: Cat;
  /** Curse: survives the grindstone. */
  curse?: boolean;
  /** Not offered by the enchanting table (Mending, Wind Burst). */
  treasure?: boolean;
  /** Mutually exclusive group names. */
  excl?: string[];
}

const D: EnchDef[] = [];
function d(id: number, key: string, name: string, max: number, weight: number,
  min: [number, number], mx: [number, number], primary: Cat, extra: Partial<EnchDef> = {}): void {
  D[id] = { id, key, name, max, weight, minBase: min[0], minPer: min[1], maxBase: mx[0], maxPer: mx[1], primary, ...extra };
}

const PROT = ['armor_prot'];
const DMG = ['damage'];
d(E.PROTECTION, 'protection', 'Protection', 4, 10, [1, 11], [12, 11], 'armor', { excl: PROT });
d(E.FIRE_PROTECTION, 'fire_protection', 'Fire Protection', 4, 5, [10, 8], [18, 8], 'armor', { excl: PROT });
d(E.FEATHER_FALLING, 'feather_falling', 'Feather Falling', 4, 5, [5, 6], [11, 6], 'feet');
d(E.BLAST_PROTECTION, 'blast_protection', 'Blast Protection', 4, 2, [5, 8], [13, 8], 'armor', { excl: PROT });
d(E.PROJECTILE_PROTECTION, 'projectile_protection', 'Projectile Protection', 4, 5, [3, 6], [9, 6], 'armor', { excl: PROT });
d(E.RESPIRATION, 'respiration', 'Respiration', 3, 2, [10, 10], [40, 10], 'head');
d(E.AQUA_AFFINITY, 'aqua_affinity', 'Aqua Affinity', 1, 2, [1, 0], [41, 0], 'head');
d(E.THORNS, 'thorns', 'Thorns', 3, 1, [10, 20], [60, 20], 'chest', { supported: 'armor' });
d(E.DEPTH_STRIDER, 'depth_strider', 'Depth Strider', 3, 2, [10, 10], [25, 10], 'feet', { excl: ['boots_liquid'] });
d(E.SHARPNESS, 'sharpness', 'Sharpness', 5, 10, [1, 11], [21, 11], 'sharp', { excl: DMG });
d(E.SMITE, 'smite', 'Smite', 5, 5, [5, 8], [25, 8], 'sharp', { excl: DMG, supported: 'sharp' });
d(E.BANE_OF_ARTHROPODS, 'bane_of_arthropods', 'Bane of Arthropods', 5, 5, [5, 8], [25, 8], 'sharp', { excl: DMG });
d(E.KNOCKBACK, 'knockback', 'Knockback', 2, 5, [5, 20], [55, 20], 'sword', { supported: 'spear' });
d(E.FIRE_ASPECT, 'fire_aspect', 'Fire Aspect', 2, 2, [10, 20], [60, 20], 'sword', { supported: 'fire_aspect' }); // fire_aspect includes spears
d(E.LOOTING, 'looting', 'Looting', 3, 2, [15, 9], [65, 9], 'sword', { supported: 'spear' });
d(E.SWEEPING_EDGE, 'sweeping_edge', 'Sweeping Edge', 3, 2, [5, 9], [20, 9], 'sword');
d(E.EFFICIENCY, 'efficiency', 'Efficiency', 5, 10, [1, 10], [51, 10], 'mining');
d(E.SILK_TOUCH, 'silk_touch', 'Silk Touch', 1, 1, [15, 0], [65, 0], 'mining_loot', { excl: ['mining_exclusive'] });
d(E.UNBREAKING, 'unbreaking', 'Unbreaking', 3, 5, [5, 8], [55, 8], 'durability');
d(E.FORTUNE, 'fortune', 'Fortune', 3, 2, [15, 9], [65, 9], 'mining_loot', { excl: ['mining_exclusive'] });
d(E.POWER, 'power', 'Power', 5, 10, [1, 10], [16, 10], 'bow');
d(E.PUNCH, 'punch', 'Punch', 2, 2, [12, 20], [37, 20], 'bow');
d(E.FLAME, 'flame', 'Flame', 1, 2, [20, 0], [50, 0], 'bow');
d(E.INFINITY, 'infinity', 'Infinity', 1, 1, [20, 0], [50, 0], 'bow', { excl: ['bow'] });
d(E.MENDING, 'mending', 'Mending', 1, 2, [25, 25], [75, 25], 'durability', { treasure: true, excl: ['bow'] });
d(E.DENSITY, 'density', 'Density', 5, 5, [5, 8], [25, 8], 'mace', { excl: DMG });
d(E.BREACH, 'breach', 'Breach', 4, 2, [15, 9], [65, 9], 'mace', { excl: DMG });
d(E.WIND_BURST, 'wind_burst', 'Wind Burst', 3, 2, [15, 9], [65, 9], 'mace', { treasure: true });
d(E.QUICK_CHARGE, 'quick_charge', 'Quick Charge', 3, 5, [12, 20], [50, 0], 'crossbow');
d(E.MULTISHOT, 'multishot', 'Multishot', 1, 2, [20, 0], [50, 0], 'crossbow', { excl: ['crossbow'] });
d(E.PIERCING, 'piercing', 'Piercing', 4, 10, [1, 10], [50, 0], 'crossbow', { excl: ['crossbow'] });
d(E.LOYALTY, 'loyalty', 'Loyalty', 3, 5, [12, 7], [50, 0], 'trident', { excl: ['riptide'] });
d(E.IMPALING, 'impaling', 'Impaling', 5, 2, [1, 8], [21, 8], 'trident');
d(E.RIPTIDE, 'riptide', 'Riptide', 3, 2, [17, 7], [50, 0], 'trident', { excl: ['riptide'] });
d(E.CHANNELING, 'channeling', 'Channeling', 1, 1, [25, 0], [50, 0], 'trident', { excl: ['riptide'] });
d(E.BINDING_CURSE, 'binding_curse', 'Curse of Binding', 1, 1, [25, 0], [50, 0], 'armor', { treasure: true, curse: true, supported: 'durability' });
d(E.VANISHING_CURSE, 'vanishing_curse', 'Curse of Vanishing', 1, 1, [25, 0], [50, 0], 'durability', { treasure: true, curse: true });

// Lunge I-III (spear only): vanilla weight 5, min cost 5/+8, max 25/+8 (est.)
d(E.LUNGE, 'lunge', 'Lunge', 3, 5, [5, 8], [25, 8], 'spear');
d(E.LURE, 'lure', 'Lure', 3, 2, [15, 9], [65, 9], 'fishing_rod');
d(E.LUCK_OF_THE_SEA, 'luck_of_the_sea', 'Luck of the Sea', 3, 2, [15, 9], [65, 9], 'fishing_rod');

export const ENCHANTS: readonly EnchDef[] = D;
export const enchDef = (id: number): EnchDef | undefined => D[id];

export const minCost = (e: EnchDef, lvl: number): number => e.minBase + e.minPer * (lvl - 1);
export const maxCost = (e: EnchDef, lvl: number): number => e.maxBase + e.maxPer * (lvl - 1);

/** Two enchantments may share an item (vanilla `exclusive_set`). */
export function compatible(a: number, b: number): boolean {
  if (a === b) return false;
  const ea = D[a], eb = D[b];
  if (!ea || !eb || !ea.excl || !eb.excl) return true;
  return !ea.excl.some((g) => eb.excl!.includes(g));
}

// --- item categories ------------------------------------------------------------

/** Vanilla enchantability of an item (0 = not enchantable). */
export function enchantability(itemId: number): number {
  const n = itemNameOf(itemId);
  if (!n) return 0;
  if (n === 'BOOK' || n === 'BOW' || n === 'TRIDENT' || n === 'CROSSBOW' || n === 'FISHING_ROD') return 1;
  if (n === 'MACE') return 15;
  if (n.endsWith('_SPEAR')) return ({ WOODEN: 15, STONE: 5, COPPER: 13, IRON: 14, GOLDEN: 22, DIAMOND: 10, NETHERITE: 15 } as Record<string, number>)[n.split('_')[0]] ?? 0;
  const mat = n.split('_')[0];
  const armor = /_(HELMET|CHESTPLATE|LEGGINGS|BOOTS)$/.test(n);
  const tool = /_(SWORD|PICKAXE|AXE|SHOVEL|HOE)$/.test(n);
  if (armor) return ({ LEATHER: 15, COPPER: 8, IRON: 9, GOLDEN: 25, DIAMOND: 10, NETHERITE: 15 } as Record<string, number>)[mat] ?? 0;
  if (tool) return ({ WOODEN: 15, STONE: 5, COPPER: 13, IRON: 14, GOLDEN: 22, DIAMOND: 10, NETHERITE: 15 } as Record<string, number>)[mat] ?? 0;
  return 0;
}

export function inCat(itemId: number, c: Cat): boolean {
  const n = itemNameOf(itemId) ?? '';
  switch (c) {
    case 'head': return n.endsWith('_HELMET');
    case 'chest': return n.endsWith('_CHESTPLATE');
    case 'legs': return n.endsWith('_LEGGINGS');
    case 'feet': return n.endsWith('_BOOTS');
    case 'armor': return /_(HELMET|CHESTPLATE|LEGGINGS|BOOTS)$/.test(n);
    case 'sword': return n.endsWith('_SWORD');
    case 'sharp': return n.endsWith('_SWORD') || n.endsWith('_AXE') || n.endsWith('_SPEAR');
    case 'spear': return n.endsWith('_SPEAR');
    case 'fire_aspect': return n.endsWith('_SWORD') || n === 'MACE' || n.endsWith('_SPEAR');
    case 'mining': return /_(PICKAXE|AXE|SHOVEL|HOE)$/.test(n) || n === 'SHEARS';
    case 'mining_loot': return /_(PICKAXE|AXE|SHOVEL|HOE)$/.test(n);
    case 'bow': return n === 'BOW';
    case 'mace': return n === 'MACE';
    case 'trident': return n === 'TRIDENT';
    case 'crossbow': return n === 'CROSSBOW';
    case 'fishing_rod': return n === 'FISHING_ROD';
    case 'durability':
      return /_(HELMET|CHESTPLATE|LEGGINGS|BOOTS|SWORD|PICKAXE|AXE|SHOVEL|HOE|SPEAR)$/.test(n)
        || ['BOW', 'CROSSBOW', 'SHEARS', 'SHIELD', 'FLINT_AND_STEEL', 'MACE', 'TRIDENT', 'FISHING_ROD'].includes(n);
  }
}

/** Enchanting table may roll `e` for this item (books take everything). */
export function tableCanApply(e: EnchDef, itemId: number): boolean {
  if (itemId === I.BOOK) return true;
  // vanilla `#enchantable/melee_weapon`: spears roll Knockback / Fire Aspect / Looting at the table like swords
  if (inCat(itemId, 'spear') && (e.id === E.KNOCKBACK || e.id === E.FIRE_ASPECT || e.id === E.LOOTING)) return true;
  return inCat(itemId, e.primary);
}
/** Anvil / book may apply `e` to this item. */
export function canApply(e: EnchDef, itemId: number): boolean {
  if (itemId === I.ENCHANTED_BOOK) return true;
  return inCat(itemId, e.primary) || (e.supported ? inCat(itemId, e.supported) : false);
}

export const UNDEAD = new Set(['zombie', 'skeleton', 'zombified_piglin', 'wither_skeleton', 'husk', 'stray', 'drowned']);
export const ARTHROPOD = new Set(['spider', 'cave_spider', 'silverfish', 'endermite', 'bee']);
